import { Navigate, createBrowserRouter, RouterProvider } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Sentry } from "@/lib/sentry";
import { ThemeProvider } from "@/lib/theme";
import { AuthProvider, useAuth } from "@/lib/auth";
import { RootLayout } from "@/components/layout/RootLayout";
import { LandingPage } from "@/pages/Landing";
import { VenuesPage } from "@/pages/Venues";
import { AboutPage } from "@/pages/About";
import { AdvertisePage } from "@/pages/Advertise";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicy";
import { TermsPage } from "@/pages/Terms";
import { VenueTermsPage } from "@/pages/VenueTerms";
import { CookiePolicyPage } from "@/pages/CookiePolicy";
import { CommunityRulesPage } from "@/pages/CommunityRules";
import { RefundPolicyPage } from "@/pages/RefundPolicy";
import { CourtsPage } from "@/pages/Courts";
import { CourtDetailPage } from "@/pages/CourtDetail";
import { MatchDetailPage } from "@/pages/MatchDetail";
import { NotFoundPage } from "@/pages/NotFound";
import { LoginPage } from "@/pages/Login";
import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { AuthResetPasswordPage } from "@/pages/AuthResetPassword";
import { AuthCallbackPage } from "@/pages/AuthCallback";
import { DashboardLayout } from "@/pages/dashboard/DashboardLayout";
import { AdminOverview } from "@/pages/dashboard/admin/AdminOverview";
import { AdminVenues } from "@/pages/dashboard/admin/AdminVenues";
import { AdminAnnouncements } from "@/pages/dashboard/admin/AdminAnnouncements";
import { AdminUsers } from "@/pages/dashboard/admin/AdminStubs";
import { AdminPayments } from "@/pages/dashboard/admin/AdminPayments";
import { AdminFlags } from "@/pages/dashboard/admin/AdminFlags";
import { AdminNoShows } from "@/pages/dashboard/admin/AdminNoShows";
import { AdminDisputes } from "@/pages/dashboard/admin/AdminDisputes";
import { AdminContent } from "@/pages/dashboard/admin/AdminContent";
import { AdminMatches } from "@/pages/dashboard/admin/AdminMatches";
import { AdminSettings } from "@/pages/dashboard/admin/AdminSettings";
import {
  VenueOverview,
  VenueSettings,
} from "@/pages/dashboard/venue/VenueTabs";
import { VenueCourtsTab } from "@/pages/dashboard/venue/VenueCourtsTab";
import { VenueBookingsTab } from "@/pages/dashboard/venue/VenueBookingsTab";
import { VenuePayoutsTab } from "@/pages/dashboard/venue/VenuePayoutsTab";
import { VenueIntegrationsTab } from "@/pages/dashboard/venue/VenueIntegrationsTab";
import { AdminAuditLog } from "@/pages/dashboard/admin/AdminAuditLog";
import { AdminAnalytics } from "@/pages/dashboard/admin/AdminAnalytics";
import { VenueAnalytics } from "@/pages/dashboard/venue/VenueAnalytics";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { PageLoader } from "@/components/brand/PageLoader";

/**
 * Auth gate for /dashboard/* - redirects to /login if unauthenticated.
 * Role-based branching happens inside DashboardLayout so the URL stays
 * the same regardless of role.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { loading, user } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <PageLoader size={64} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * /dashboard landing - picks the right tab set based on role so the
 * user lands on "Overview" regardless of whether they're admin or
 * venue_admin. Same URL, different content.
 */
function DashboardIndex() {
  return <Navigate to="overview" replace />;
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: "/", element: <LandingPage /> },
      { path: "/venues", element: <VenuesPage /> },
      { path: "/about", element: <AboutPage /> },
      { path: "/advertise", element: <AdvertisePage /> },
      { path: "/privacy-policy", element: <PrivacyPolicyPage /> },
      { path: "/terms", element: <TermsPage /> },
      { path: "/venue-terms", element: <VenueTermsPage /> },
      { path: "/cookie-policy", element: <CookiePolicyPage /> },
      { path: "/community-rules", element: <CommunityRulesPage /> },
      { path: "/refunds", element: <RefundPolicyPage /> },
      // Older link from the mobile app used /refund-policy; keep both
      // pointing to the same page so existing email links don't 404.
      { path: "/refund-policy", element: <RefundPolicyPage /> },
      { path: "/courts", element: <CourtsPage /> },
      { path: "/courts/:id", element: <CourtDetailPage /> },
      { path: "/matches/:id", element: <MatchDetailPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
  // Login sits outside the marketing layout (no Header / Footer).
  { path: "/login", element: <LoginPage />, errorElement: <ErrorBoundary /> },
  // Password-reset entry point. Lives at the root (not under /login)
  // because mobile users hit this directly when their in-app reset
  // email's custom-scheme deep link fails to resolve.
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
    errorElement: <ErrorBoundary />,
  },
  // Landing page for the reset email - handles the ?code= exchange and
  // shows the new-password form. Must be on the same origin that the
  // user requested the reset from (PKCE flow stores the code_verifier
  // in browser localStorage).
  {
    path: "/auth/reset-password",
    element: <AuthResetPasswordPage />,
    errorElement: <ErrorBoundary />,
  },
  // Universal callback for sign-up + email-change verification. Safety
  // net for when the mobile deep link (whistll://auth/callback) fails
  // to resolve in the email client - replaces a blank/404 page with a
  // branded "open this in the app" message.
  {
    path: "/auth/callback",
    element: <AuthCallbackPage />,
    errorElement: <ErrorBoundary />,
  },
  // Dashboard + all nested tabs. Auth-gated. Single URL regardless of
  // whether the user is an admin or a venue partner - content diverges
  // inside DashboardLayout based on profile.role.
  {
    path: "/dashboard",
    element: (
      <RequireAuth>
        <DashboardLayout />
      </RequireAuth>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <DashboardIndex /> },
      // The route TREE is flat - DashboardLayout shows different
      // sidebar items based on role, and RLS stops a venue admin from
      // loading admin-tab data even if they manipulate the URL.
      { path: "overview", element: <RoleOverview /> },
      // Analytics URL is shared between roles - the wrapper renders the
      // admin or venue version based on the signed-in user's role, same
      // pattern as RoleOverview above.
      { path: "analytics", element: <RoleAnalytics /> },
      // Admin-only tabs
      { path: "venues", element: <AdminVenues /> },
      { path: "users", element: <AdminUsers /> },
      { path: "matches", element: <AdminMatches /> },
      { path: "content", element: <AdminContent /> },
      { path: "flags", element: <AdminFlags /> },
      { path: "disputes", element: <AdminDisputes /> },
      { path: "no-shows", element: <AdminNoShows /> },
      { path: "payments", element: <AdminPayments /> },
      { path: "announcements", element: <AdminAnnouncements /> },
      { path: "audit-log", element: <AdminAuditLog /> },
      // Venue-admin tabs
      { path: "courts", element: <VenueCourtsTab /> },
      { path: "bookings", element: <VenueBookingsTab /> },
      { path: "payouts", element: <VenuePayoutsTab /> },
      { path: "integrations", element: <VenueIntegrationsTab /> },
      { path: "settings", element: <RoleSettings /> },
    ],
  },
]);

/** "Overview" page differs per role. */
function RoleOverview() {
  const { isAdmin, isVenueAdmin } = useAuth();
  if (isAdmin) return <AdminOverview />;
  if (isVenueAdmin) return <VenueOverview />;
  // DashboardLayout will already have caught this case, but be safe.
  return null;
}

/** "Analytics" page differs per role - admin gets the platform-wide
 *  KPI dashboard, venues get their own venue-scoped retention view. */
function RoleAnalytics() {
  const { isAdmin, isVenueAdmin } = useAuth();
  if (isAdmin) return <AdminAnalytics />;
  if (isVenueAdmin) return <VenueAnalytics />;
  return null;
}

/** "Settings" page differs per role - admin sees account-only, venue
 *  sees venue profile + account. */
function RoleSettings() {
  const { isAdmin, isVenueAdmin } = useAuth();
  if (isAdmin) return <AdminSettings />;
  if (isVenueAdmin) return <VenueSettings />;
  return null;
}

const SentryBoundary = Sentry.ErrorBoundary;

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SentryBoundary
          fallback={({ error, resetError }) => (
            <div className="mx-auto max-w-lg p-10 text-center">
              <h1 className="text-2xl font-semibold">Something broke.</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {error instanceof Error ? error.message : "Unknown error"}
              </p>
              <button
                onClick={resetError}
                className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              >
                Try again
              </button>
            </div>
          )}
        >
          <RouterProvider router={router} />
          <Toaster
            position="top-right"
            toastOptions={{
              className:
                "!bg-card !text-card-foreground !border !border-border !shadow-lg",
            }}
          />
        </SentryBoundary>
      </AuthProvider>
    </ThemeProvider>
  );
}
